/**
 * KOT and KDS contracts (03-modules.md §5, 04-data-model.md "kitchen", 05-api.md
 * "Kitchen and printing"): stations, printers, routing, print templates, KOTs and
 * their lines, print jobs, KDS tickets, and the kitchen's outbox events.
 *
 * Print documents themselves are `print.ts` (PrintDoc). Quantities are integer
 * milli-units (1000 = one), as on orders.
 *
 * Defined without importing org.ts: org's bootstrap bundle carries the stations
 * and printers (org.ts imports these schemas).
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { PAPER_WIDTHS_MM, printDocSchema } from './print.js';

const nameSchema = z.string().trim().min(1).max(60);
const sortSchema = z.int().min(0).max(10_000);
const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

// ---------------------------------------------------------------------------
// Stations (ticket groups) and printers

export const STATION_KINDS = ['kitchen', 'bar', 'dessert'] as const;
export type StationKind = (typeof STATION_KINDS)[number];

export const PRINTER_CONNECTIONS = ['usb', 'serial', 'lan', 'bluetooth', 'builtin'] as const;
export type PrinterConnection = (typeof PRINTER_CONNECTIONS)[number];

export const paperWidthSchema = z.union([
  z.literal(PAPER_WIDTHS_MM[0]),
  z.literal(PAPER_WIDTHS_MM[1]),
]);

/** Copies of each KOT a station prints on one of its printers. */
export const stationPrinterSchema = z.object({
  printerId: uuidSchema,
  copies: z.int().min(1).max(5),
});

export const stationSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  kind: z.enum(STATION_KINDS),
  /** Its tickets show on KDS screens (`outlet:{o}:kds:{stationId}`). */
  kdsEnabled: z.boolean(),
  /** Items no routing rule names go here (one per outlet). */
  isDefault: z.boolean(),
  sort: z.int(),
  printers: z.array(stationPrinterSchema),
  ...archivable,
});
export type StationView = z.output<typeof stationSchema>;

const stationPrintersInput = z
  .array(stationPrinterSchema.extend({ copies: z.int().min(1).max(5).optional() }))
  .max(10)
  .refine((p) => new Set(p.map((x) => x.printerId)).size === p.length, {
    message: 'Each printer at most once',
  });

const stationFields = {
  name: nameSchema,
  kind: z.enum(STATION_KINDS),
  kdsEnabled: z.boolean(),
  isDefault: z.boolean(),
  sort: sortSchema,
  printers: stationPrintersInput,
};

export const createStationSchema = z.object({
  id: uuidSchema.optional(),
  name: stationFields.name,
  kind: stationFields.kind.optional(),
  kdsEnabled: stationFields.kdsEnabled.optional(),
  isDefault: stationFields.isDefault.optional(),
  sort: sortSchema.optional(),
  printers: stationFields.printers.optional(),
});
export const updateStationSchema = z
  .object(stationFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const stationParamsSchema = z.object({ outletId: uuidSchema, stationId: uuidSchema });

export const printerSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  connection: z.enum(PRINTER_CONNECTIONS),
  /** IP:port (LAN), device path, Bluetooth MAC or vendor id; the owner terminal's business. */
  address: z.string().nullable(),
  paperMm: paperWidthSchema,
  /**
   * The terminal (till, KDS box or hub) the printer is attached to: its print jobs
   * go to `terminal:{ownerTerminalId}`. Null: nobody can print on it yet.
   */
  ownerTerminalId: uuidSchema.nullable(),
  /** Bills print here (the billing terminal's own bill printer first). */
  printsBills: z.boolean(),
  ...archivable,
});
export type PrinterView = z.output<typeof printerSchema>;

const printerFields = {
  name: nameSchema,
  connection: z.enum(PRINTER_CONNECTIONS),
  address: z.string().trim().min(1).max(200).nullable(),
  paperMm: paperWidthSchema,
  ownerTerminalId: uuidSchema.nullable(),
  printsBills: z.boolean(),
};

export const createPrinterSchema = z.object({
  id: uuidSchema.optional(),
  name: printerFields.name,
  connection: printerFields.connection,
  address: printerFields.address.optional(),
  paperMm: printerFields.paperMm.optional(),
  ownerTerminalId: printerFields.ownerTerminalId.optional(),
  printsBills: printerFields.printsBills.optional(),
});
export const updatePrinterSchema = z
  .object(printerFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const printerParamsSchema = z.object({ outletId: uuidSchema, printerId: uuidSchema });

// ---------------------------------------------------------------------------
// Routing: which station gets an item (`PUT {o}/stations/{id}/products`)

export const ROUTE_KINDS = ['product', 'category', 'section_copy'] as const;
export type RouteKind = (typeof ROUTE_KINDS)[number];

/**
 * One routing rule of a station:
 * - `product`: this item goes to the station;
 * - `category`: items of this menu category go to the station (unless a product
 *   rule names another);
 * - either one with `sectionId`: only for orders seated in that section (it wins
 *   over the same rule without a section);
 * - `section_copy` (`sectionId` only): every KOT of an order in that section also
 *   prints a copy here (03-modules.md §5 "duplicates for sections that need them").
 */
export const stationRouteSchema = z.object({
  kind: z.enum(ROUTE_KINDS),
  productId: uuidSchema.nullable(),
  categoryId: uuidSchema.nullable(),
  sectionId: uuidSchema.nullable(),
});
export type StationRouteView = z.output<typeof stationRouteSchema>;

export const MAX_ROUTES_PER_STATION = 2_000;

const routeInputSchema = z
  .object({
    productId: uuidSchema.optional(),
    categoryId: uuidSchema.optional(),
    sectionId: uuidSchema.optional(),
  })
  .refine((r) => !(r.productId !== undefined && r.categoryId !== undefined), {
    message: 'A rule names a product or a category, not both',
  })
  .refine(
    (r) => r.productId !== undefined || r.categoryId !== undefined || r.sectionId !== undefined,
    { message: 'Name a product, a category or (for section copies) a section' },
  );

/** `PUT {o}/stations/{id}/products`: the station's rules, replaced as a whole. */
export const setStationRoutesSchema = z.object({
  routes: z.array(routeInputSchema).max(MAX_ROUTES_PER_STATION),
});

export const stationRoutesSchema = z.object({
  stationId: uuidSchema,
  routes: z.array(stationRouteSchema),
});

// ---------------------------------------------------------------------------
// Print templates

export const PRINT_TEMPLATE_KINDS = ['kot', 'bill', 'test', 'qr'] as const;
export type PrintTemplateKind = (typeof PRINT_TEMPLATE_KINDS)[number];

const templateLine = z.string().trim().max(48);

/**
 * What an outlet may change on a printed document (a settings bag, not a template
 * language): extra header and footer lines, larger item names (KOT), and whether
 * modifiers and notes print. Unknown keys are dropped.
 */
export const printTemplateBodySchema = z.object({
  header: z.array(templateLine).max(6),
  footer: z.array(templateLine).max(6),
  /** KOT: double-height item lines for a kitchen across the room. */
  largeItems: z.boolean(),
  showModifiers: z.boolean(),
  showNotes: z.boolean(),
});
export type PrintTemplateBody = z.output<typeof printTemplateBodySchema>;

export const DEFAULT_PRINT_TEMPLATE_BODY: PrintTemplateBody = Object.freeze({
  header: [],
  footer: [],
  largeItems: false,
  showModifiers: true,
  showNotes: true,
}) as PrintTemplateBody;

export const printTemplateSchema = z.object({
  kind: z.enum(PRINT_TEMPLATE_KINDS),
  body: printTemplateBodySchema,
  /** No outlet template: the built-in default is used. */
  isDefault: z.boolean(),
  version: z.int(),
  updatedAt: isoTimestampSchema.nullable(),
});
export type PrintTemplateView = z.output<typeof printTemplateSchema>;

export const printTemplateListSchema = z.object({ data: z.array(printTemplateSchema) });
export const setPrintTemplateSchema = printTemplateBodySchema.partial();
export const printTemplateParamsSchema = z.object({
  outletId: uuidSchema,
  kind: z.enum(PRINT_TEMPLATE_KINDS),
});

// ---------------------------------------------------------------------------
// KOTs

export const KOT_KINDS = ['new', 'incremental', 'void', 'reprint'] as const;
export type KotKind = (typeof KOT_KINDS)[number];

/**
 * `printing`: its print jobs are out; `printed`: every job printed; `displayed`: a
 * KDS-only station (no printer) has it on screen; `failed`: a job failed for good
 * (or no printer and no KDS), its items can be sent again.
 */
export const KOT_STATUSES = ['printing', 'printed', 'displayed', 'failed'] as const;
export type KotStatus = (typeof KOT_STATUSES)[number];

export const kotLineSchema = z.object({
  orderLineId: uuidSchema,
  productId: uuidSchema,
  name: z.string(),
  qtyMilli: z.int(),
  note: z.string().nullable(),
  seat: z.int().nullable(),
  course: z.int().nullable(),
  modifiers: z.array(z.object({ kind: z.string(), name: z.string(), qtyMilli: z.int() })),
  /** Full-mode KOTs repeat earlier items: false for those, true for this round's. */
  isNew: z.boolean(),
  prepMinutes: z.int().nullable(),
});
export type KotLineView = z.output<typeof kotLineSchema>;

export const kotSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  businessDate: businessDateSchema,
  /** Per outlet per business day, gap-free; a reprint keeps the original's number. */
  kotNo: z.int(),
  stationId: uuidSchema,
  stationName: z.string(),
  kind: z.enum(KOT_KINDS),
  /** A section copy (03-modules.md §5): printed for information, it confirms nothing. */
  copy: z.boolean(),
  /** The orders KOT request (`order.kot_requested` kotId) it answers. */
  requestId: uuidSchema.nullable(),
  reprintOfKotId: uuidSchema.nullable(),
  status: z.enum(KOT_STATUSES),
  failureReason: z.string().nullable(),
  orderType: z.string(),
  tableNames: z.array(z.string()),
  /** Where the order came from: the outlet's order source name, else the source tag (Swiggy, QR…). */
  sourceLabel: z.string().nullable(),
  /** The order source's packaging note, on takeaway, delivery and aggregator orders. */
  packagingNote: z.string().nullable(),
  /** Who sent it: the staff member's name and the terminal (`Ravi @ Captain 2`). */
  sentBy: z.string().nullable(),
  /** A void KOT: the reason the item was taken back (the reason label, else its code). */
  voidReason: z.string().nullable(),
  createdBy: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  printedAt: isoTimestampSchema.nullable(),
  bumpedAt: isoTimestampSchema.nullable(),
  lines: z.array(kotLineSchema),
});
export type KotView = z.output<typeof kotSchema>;

export const kotListQuerySchema = z.object({
  orderId: uuidSchema.optional(),
  status: z.enum(KOT_STATUSES).optional(),
  businessDate: businessDateSchema.optional(),
});
export const kotListSchema = z.object({ data: z.array(kotSchema) });
export const kotParamsSchema = z.object({ outletId: uuidSchema, kotId: uuidSchema });

/** `POST {o}/kots/{id}/reprint`: at the KOT's station printers, or the one named. */
export const reprintKotSchema = z.object({ printerId: uuidSchema.optional() });

/** `POST {o}/orders/{id}/print-bill`: the bill printer of this terminal, or the one named. */
export const printBillSchema = z.object({ printerId: uuidSchema.optional() });

/** `POST {o}/tables/{id}/print-qr`. */
export const printTableQrSchema = z.object({ printerId: uuidSchema });

// ---------------------------------------------------------------------------
// Print jobs

export const PRINT_JOB_KINDS = ['kot', 'bill', 'test', 'qr', 'report'] as const;
export type PrintJobKind = (typeof PRINT_JOB_KINDS)[number];

/**
 * `queued`: waiting to be sent (first time, or after a failed attempt's backoff);
 * `sent`: delivered to the owner terminal, waiting for its confirmation;
 * `printed` / `failed`: final.
 */
export const PRINT_JOB_STATUSES = ['queued', 'sent', 'printed', 'failed'] as const;
export type PrintJobStatus = (typeof PRINT_JOB_STATUSES)[number];

export const PRINT_REF_TYPES = ['kot', 'order', 'printer', 'table'] as const;

export const printJobSummarySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  printerId: uuidSchema,
  printerName: z.string(),
  targetTerminalId: uuidSchema.nullable(),
  kind: z.enum(PRINT_JOB_KINDS),
  status: z.enum(PRINT_JOB_STATUSES),
  attempts: z.int(),
  maxAttempts: z.int(),
  refType: z.enum(PRINT_REF_TYPES).nullable(),
  refId: uuidSchema.nullable(),
  watermark: z.string().nullable(),
  lastError: z.string().nullable(),
  createdAt: isoTimestampSchema,
  sentAt: isoTimestampSchema.nullable(),
  printedAt: isoTimestampSchema.nullable(),
  failedAt: isoTimestampSchema.nullable(),
});
export type PrintJobSummary = z.output<typeof printJobSummarySchema>;

export const printJobSchema = printJobSummarySchema.extend({ doc: printDocSchema });
export type PrintJobView = z.output<typeof printJobSchema>;

export const printJobListQuerySchema = z.object({
  /** `mine`: jobs waiting for this terminal (queued or sent), oldest first. */
  scope: z.enum(['mine', 'failed', 'recent']).default('mine'),
});
export const printJobListSchema = z.object({ data: z.array(printJobSummarySchema) });
export const printJobParamsSchema = z.object({ outletId: uuidSchema, jobId: uuidSchema });

/** `POST {o}/print-jobs/{id}/confirm` from the device that printed (or failed to). */
export const confirmPrintSchema = z
  .object({
    ok: z.boolean(),
    error: z.string().trim().min(1).max(300).optional(),
  })
  .refine((v) => v.ok || v.error !== undefined, {
    message: 'Say what went wrong',
    path: ['error'],
  });

// ---------------------------------------------------------------------------
// KDS

export const kdsLineSchema = kotLineSchema.extend({
  /** The item's status on the order (orders owns it): sent, preparing, ready, served, void. */
  status: z.string(),
});

export const kdsTicketSchema = kotSchema.omit({ lines: true }).extend({
  lines: z.array(kdsLineSchema),
  /** The longest prep time of its items (minutes), for the green/amber/red timer. */
  prepMinutes: z.int().nullable(),
});
export type KdsTicketView = z.output<typeof kdsTicketSchema>;

export const kdsTicketsSchema = z.object({
  /** The server's clock, so screens time tickets against it. */
  now: isoTimestampSchema,
  /** Not bumped, oldest first (expeditor: every KDS station of the outlet). */
  open: z.array(kdsTicketSchema),
  /** Bumped in the last hours, newest first: what "recall" brings back. */
  recentlyBumped: z.array(kdsTicketSchema),
});

export const kdsStationParamsSchema = z.object({ outletId: uuidSchema, stationId: uuidSchema });

/** `POST {o}/kots/{id}/bump`: its items (or the named ones) to ready, or to preparing. */
export const bumpTicketSchema = z.object({
  lineIds: z.array(uuidSchema).min(1).max(200).optional(),
  to: z.enum(['preparing', 'ready']).optional(),
});
/** `POST {o}/kots/{id}/recall`: ready items back to preparing; the ticket shows again. */
export const recallTicketSchema = z.object({
  lineIds: z.array(uuidSchema).min(1).max(200).optional(),
});
/** `POST {o}/kots/{id}/serve`: the ticket's ready items are served. */
export const serveTicketSchema = recallTicketSchema;

/** What a KDS action did: the items it moved, and whether the ticket is now off the screen. */
export const ticketActionResultSchema = z.object({
  kotId: uuidSchema,
  lineIds: z.array(uuidSchema),
  bumped: z.boolean(),
});

// ---------------------------------------------------------------------------
// Events

const setupChange = z.enum(['created', 'updated', 'archived']);

export const stationChangedV1 = defineEvent(
  'station.changed',
  1,
  z.object({ stationId: uuidSchema, change: setupChange, version: z.int() }),
);

export const printerChangedV1 = defineEvent(
  'printer.changed',
  1,
  z.object({ printerId: uuidSchema, change: setupChange, version: z.int() }),
);

export const stationRoutesChangedV1 = defineEvent(
  'station_routes.changed',
  1,
  z.object({ stationId: uuidSchema, routeCount: z.int() }),
);

export const printTemplateChangedV1 = defineEvent(
  'print_template.changed',
  1,
  z.object({
    kind: z.enum(PRINT_TEMPLATE_KINDS),
    change: z.enum(['set', 'reset']),
    version: z.int(),
  }),
);

const kotRef = {
  kotId: uuidSchema,
  kotNo: z.int(),
  businessDate: businessDateSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  stationId: uuidSchema,
  kind: z.enum(KOT_KINDS),
  copy: z.boolean(),
};

/** A KOT was created (and its print jobs queued): KDS screens of its station show it. */
export const kotCreatedV1 = defineEvent(
  'kot.created',
  1,
  z.object({
    ...kotRef,
    requestId: uuidSchema.nullable(),
    reprintOfKotId: uuidSchema.nullable(),
    status: z.enum(KOT_STATUSES),
    lineCount: z.int(),
  }),
);

/** Every print job of the KOT printed (or a KDS-only station shows it). */
export const kotPrintedV1 = defineEvent(
  'kot.printed',
  1,
  z.object({ ...kotRef, status: z.enum(['printed', 'displayed']) }),
);

/** The KOT could not reach the kitchen: its items can be sent again. */
export const kotPrintFailedV1 = defineEvent(
  'kot.print_failed',
  1,
  z.object({ ...kotRef, reason: z.string() }),
);

export const ticketBumpedV1 = defineEvent(
  'ticket.bumped',
  1,
  z.object({
    ...kotRef,
    lineIds: z.array(uuidSchema),
    to: z.enum(['preparing', 'ready', 'served']),
  }),
);

export const ticketRecalledV1 = defineEvent(
  'ticket.recalled',
  1,
  z.object({ ...kotRef, lineIds: z.array(uuidSchema) }),
);

const jobRef = {
  jobId: uuidSchema,
  printerId: uuidSchema,
  kind: z.enum(PRINT_JOB_KINDS),
  refType: z.enum(PRINT_REF_TYPES).nullable(),
  refId: uuidSchema.nullable(),
};

/** A print job is on its way to the printer's owner terminal (`terminal:{id}`). */
export const printJobDispatchedV1 = defineEvent(
  'print_job.dispatched',
  1,
  z.object({ ...jobRef, terminalId: uuidSchema, attempt: z.int() }),
);

export const printJobPrintedV1 = defineEvent(
  'print_job.printed',
  1,
  z.object({ ...jobRef, attempts: z.int() }),
);

/** Failed for good (after its attempts, or nowhere to send it). */
export const printJobFailedV1 = defineEvent(
  'print_job.failed',
  1,
  z.object({ ...jobRef, attempts: z.int(), error: z.string() }),
);

export const KITCHEN_ALERT_REASONS = [
  /** A sent job was not confirmed within the timeout (it is sent again). */
  'print_unconfirmed',
  /** The device reported an error; it will be tried again. */
  'print_retrying',
  /** A job failed for good. */
  'print_failed',
  /** No station takes these items (no rule and no default station). */
  'no_station',
] as const;
export type KitchenAlertReason = (typeof KITCHEN_ALERT_REASONS)[number];

/** Something at the pass needs a person (POS room): a printer down, items with no station. */
export const kitchenAlertedV1 = defineEvent(
  'kitchen.alerted',
  1,
  z.object({
    reason: z.enum(KITCHEN_ALERT_REASONS),
    detail: z.string(),
    printerId: uuidSchema.nullable(),
    printerName: z.string().nullable(),
    jobId: uuidSchema.nullable(),
    jobKind: z.enum(PRINT_JOB_KINDS).nullable(),
    kotId: uuidSchema.nullable(),
    kotNo: z.int().nullable(),
    orderId: uuidSchema.nullable(),
    orderNo: z.int().nullable(),
    attempts: z.int().nullable(),
  }),
);

/** Kitchen setup a till's cached bootstrap depends on (realtime `setup.changed`). */
export const KITCHEN_SETUP_EVENTS = [
  stationChangedV1,
  printerChangedV1,
  stationRoutesChangedV1,
] as const;

// ---------------------------------------------------------------------------
// KOT number ranges for offline terminals (M4 task 5)

export const KOT_RANGE_MAX_SIZE = 500;
export const KOT_RANGE_DEFAULT_SIZE = 100;

/**
 * `POST {o}/kot-ranges`: set aside a run of today's KOT numbers for a terminal (or the
 * outlet hub) that may go offline; it numbers the KOTs it prints offline from it and
 * sends the number with the send (`kotNo` of a synced `orders.sendToKitchen`).
 */
export const reserveKotRangeSchema = z.object({
  id: uuidSchema.optional(),
  terminalId: uuidSchema.optional(),
  size: z.int().min(1).max(KOT_RANGE_MAX_SIZE).optional(),
});

export const kotRangeSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  terminalId: uuidSchema,
  businessDate: businessDateSchema,
  fromNo: z.int(),
  toNo: z.int(),
  /** Numbers of the range already carried by a synced KOT send. */
  usedCount: z.int(),
  reservedAt: isoTimestampSchema,
});
export type KotRangeView = z.output<typeof kotRangeSchema>;

export const kotRangeListQuerySchema = z.object({
  terminalId: uuidSchema.optional(),
  businessDate: businessDateSchema.optional(),
});
export const kotRangeListSchema = z.object({ data: z.array(kotRangeSchema) });

export const kotRangeReservedV1 = defineEvent(
  'kot_range.reserved',
  1,
  z.object({
    rangeId: uuidSchema,
    terminalId: uuidSchema,
    businessDate: businessDateSchema,
    fromNo: z.int(),
    toNo: z.int(),
  }),
);

/** A KOT printed offline was filed under the number its terminal printed on it. */
export const kotNumberRegisteredV1 = defineEvent(
  'kot.number_registered',
  1,
  z.object({
    rangeId: uuidSchema,
    requestId: uuidSchema,
    kotNo: z.int(),
    businessDate: businessDateSchema,
  }),
);

export const KITCHEN_EVENTS = [
  ...KITCHEN_SETUP_EVENTS,
  printTemplateChangedV1,
  kotCreatedV1,
  kotRangeReservedV1,
  kotNumberRegisteredV1,
  kotPrintedV1,
  kotPrintFailedV1,
  ticketBumpedV1,
  ticketRecalledV1,
  printJobDispatchedV1,
  printJobPrintedV1,
  printJobFailedV1,
  kitchenAlertedV1,
] as const;
